(function (globalRef) {
  const CHANGE_EVENT = "fishystuff:page-health-change";
  const LANGUAGE_CHANGE_EVENT = "fishystuff:languagechange";
  const CONSOLE_ERROR_HOOK_KEY = "__fishystuffPageHealthConsoleErrorHookInstalled";
  const FETCH_HOOK_KEY = "__fishystuffPageHealthFetchHookInstalled";
  const XHR_HOOK_KEY = "__fishystuffPageHealthXhrHookInstalled";
  const XHR_STATE_KEY = "__fishystuffPageHealthXhrState";
  const TELEMETRY_STATUS_EVENT = "fishystuff:telemetry-status-change";
  const DEFAULT_SOURCE = "page";
  const MAX_DETAIL_CHARS = 240;
  const MAX_COPY_TEXT_CHARS = 12000;
  const MAX_CONTEXT_TEXT_CHARS = 4000;
  const issues = new Map();
  const subscribers = new Set();
  let manualReportPreview = null;
  let preferencesRepository = null;
  let unsubscribePreferences = null;
  let captureActive = false;
  let captureGeneration = 0;
  let consoleErrorHook = null;
  let fetchHook = null;
  let xhrHook = null;
  let waitingForDocumentReady = false;

  function isPlainObject(value) {
    return Boolean(value) && Object.prototype.toString.call(value) === "[object Object]";
  }

  function cloneJson(value) {
    try {
      return JSON.parse(JSON.stringify(value));
    } catch (_error) {
      return {};
    }
  }

  function freezeJson(value) {
    if (!value || typeof value !== "object" || Object.isFrozen(value)) {
      return value;
    }
    for (const nested of Object.values(value)) {
      freezeJson(nested);
    }
    return Object.freeze(value);
  }

  function trimString(value) {
    return String(value ?? "").trim();
  }

  function translate(key, fallback, vars = {}) {
    const normalizedKey = trimString(key);
    const helper = globalRef.__fishystuffLanguage || globalRef.window?.__fishystuffLanguage;
    if (normalizedKey && helper && typeof helper.t === "function") {
      const translated = helper.t(normalizedKey, vars);
      if (translated && translated !== normalizedKey) {
        return translated;
      }
    }
    return fallback;
  }

  function nowIso() {
    return new Date().toISOString();
  }

  function randomId(prefix) {
    const normalizedPrefix = trimString(prefix) || "issue";
    if (globalRef.crypto && typeof globalRef.crypto.randomUUID === "function") {
      return `${normalizedPrefix}-${globalRef.crypto.randomUUID()}`;
    }
    return `${normalizedPrefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }

  function compactText(value, maxChars = MAX_DETAIL_CHARS) {
    const singleLine = trimString(value).replace(/\s+/g, " ");
    if (singleLine.length <= maxChars) {
      return singleLine;
    }
    return `${singleLine.slice(0, Math.max(0, maxChars - 3))}...`;
  }

  function boundedText(value, maxChars = MAX_COPY_TEXT_CHARS) {
    const text = trimString(value);
    if (text.length <= maxChars) {
      return text;
    }
    return `${text.slice(0, Math.max(0, maxChars - 3))}...`;
  }

  function textFingerprint(value) {
    const text = String(value ?? "");
    let hash = 2166136261;
    for (let index = 0; index < text.length; index += 1) {
      hash ^= text.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(36);
  }

  function normalizeSeverity(value) {
    const normalized = trimString(value).toLowerCase();
    if (normalized === "error" || normalized === "warning" || normalized === "info") {
      return normalized;
    }
    return "warning";
  }

  function issueIsWarning(issue) {
    return issue?.severity === "warning" || issue?.severity === "error";
  }

  function stableIssueId(source) {
    const explicitId = trimString(source.id);
    if (explicitId) {
      return explicitId;
    }
    const sourceName = trimString(source.source) || DEFAULT_SOURCE;
    const category = trimString(source.category) || "general";
    const title = trimString(source.title) || trimString(source.summary) || "Issue";
    return `${sourceName}:${category}:${title}`.toLowerCase();
  }

  function normalizeIssue(value) {
    const source = isPlainObject(value) ? value : {};
    const title = compactText(
      source.title || source.summary || translate("health.issue.default_page_title", "Page issue"),
      96,
    );
    const detail = compactText(source.detail || source.message || "", MAX_DETAIL_CHARS);
    const copyText = boundedText(
      source.copyText
      || source.originalText
      || source.rawDetail
      || source.context?.originalErrorText
      || source.context?.errorText
      || source.context?.consoleText
      || source.detail
      || source.message
      || "",
      MAX_COPY_TEXT_CHARS,
    );
    const severity = normalizeSeverity(source.severity);
    const now = nowIso();
    return {
      id: stableIssueId(source),
      severity,
      source: trimString(source.source) || DEFAULT_SOURCE,
      category: trimString(source.category) || "general",
      title,
      detail,
      copyText,
      actionLabel: compactText(source.actionLabel || "", 80),
      createdAt: trimString(source.createdAt) || now,
      updatedAt: trimString(source.updatedAt) || now,
      count: Math.max(1, Number.parseInt(source.count, 10) || 1),
      dismissed: source.dismissed === true,
      reportable: source.reportable !== false,
      context: isPlainObject(source.context) ? cloneJson(source.context) : {},
    };
  }

  function preferencesDecision() {
    if (preferencesRepository && typeof preferencesRepository.decision === "function") {
      return preferencesRepository.decision();
    }
    return {
      repositoryKind: "uninitialized",
      revision: null,
      hideNavMenu: false,
      canChange: false,
    };
  }

  function preferencesStatus(preferences = preferencesDecision()) {
    switch (preferences.repositoryKind) {
      case "missing":
      case "ready":
        return "";
      case "conflict":
        return translate(
          "health.preferences.status.conflict",
          "Diagnostic preferences changed in another tab. Reload to change them.",
        );
      case "invalid":
        return translate(
          "health.preferences.status.invalid",
          "Saved diagnostic preferences are invalid. Reset browser UI data to change them.",
        );
      case "unsupportedVersion":
        return translate(
          "health.preferences.status.unsupported_version",
          "Saved diagnostic preferences use a newer version. Reset browser UI data to change them.",
        );
      case "unavailable":
        return translate(
          "health.preferences.status.unavailable",
          "Diagnostic preferences are unavailable in this browser.",
        );
      default:
        return translate(
          "health.preferences.status.loading",
          "Diagnostic preferences are still loading.",
        );
    }
  }

  function setMenuHidden(hidden) {
    if (!preferencesRepository || typeof preferencesRepository.setMenuHidden !== "function") {
      throw new Error("Page health settings repository is unavailable.");
    }
    return preferencesRepository.setMenuHidden(hidden === true);
  }

  function subscribePreferences() {
    if (
      !captureActive
      || unsubscribePreferences !== null
      || !preferencesRepository
    ) {
      return false;
    }
    unsubscribePreferences = preferencesRepository.subscribe(() => {
      emitChange("preferences-change");
    });
    return true;
  }

  function bindPreferencesRepository(repository) {
    if (
      !repository
      || typeof repository.decision !== "function"
      || typeof repository.setMenuHidden !== "function"
      || typeof repository.subscribe !== "function"
    ) {
      throw new TypeError(
        "Page health requires the exact preferences repository contract.",
      );
    }
    if (repository === preferencesRepository) {
      subscribePreferences();
      return preferencesRepository;
    }
    if (typeof unsubscribePreferences === "function") unsubscribePreferences();
    unsubscribePreferences = null;
    preferencesRepository = repository;
    subscribePreferences();
    return preferencesRepository;
  }

  function sortedIssues() {
    const severityRank = { error: 0, warning: 1, info: 2 };
    return Array.from(issues.values()).sort((left, right) => {
      const severityDelta = (severityRank[left.severity] ?? 9) - (severityRank[right.severity] ?? 9);
      if (severityDelta) {
        return severityDelta;
      }
      return String(right.updatedAt).localeCompare(String(left.updatedAt));
    });
  }

  function activeWarnings() {
    return sortedIssues().filter((issue) => issueIsWarning(issue) && !issue.dismissed);
  }

  function issueForcesMenuVisible(issue) {
    return issue?.severity === "error";
  }

  function reportableWarnings(values) {
    return (Array.isArray(values) ? values : [])
      .filter((issue) => issueIsWarning(issue) && !issue.dismissed && issue.reportable !== false);
  }


  function telemetryStatusDetail(reason, status) {
    if (status.lastLogExport?.ok === false) {
      return translate(
        "health.telemetry.detail.export_failed",
        "The last manual report could not be delivered.",
      );
    }
    if (status.diagnosticReportsEnabled) {
      return translate(
        "health.telemetry.detail.ready",
        "Manual report delivery is available.",
      );
    }
    if (reason === "missing-logs-exporter") {
      return translate(
        "health.telemetry.detail.missing_logs_exporter",
        "Manual report delivery is not configured.",
      );
    }
    if (reason === "logs-disabled" || reason === "logs-unavailable") {
      return translate(
        "health.telemetry.detail.logs_unavailable",
        "Manual report delivery is unavailable right now.",
      );
    }
    if (reason === "disabled-by-runtime-policy") {
      return translate(
        "health.telemetry.detail.disabled_by_runtime_policy",
        "Manual reports are disabled by the runtime policy.",
      );
    }
    return translate(
      "health.telemetry.detail.uninitialized",
      "Manual report delivery has not initialized for this page.",
    );
  }


  function normalizeTelemetryStatus(value) {
    const source = isPlainObject(value) ? value : {};
    const diagnosticReportsEnabled =
      source.diagnosticReportsEnabled === true
      || source.manualDiagnosticReportsEnabled === true;
    const lastLogExport = isPlainObject(source.lastLogExport) ? cloneJson(source.lastLogExport) : null;
    const reason = trimString(source.reason || source.telemetryReason)
      || (diagnosticReportsEnabled ? "ready" : "telemetry-unavailable");
    const status = {
      initialized: source.initialized === true,
      tracingEnabled: source.tracingEnabled === true,
      metricsEnabled: source.metricsEnabled === true,
      loggingEnabled: source.loggingEnabled === true,
      diagnosticReportsEnabled,
      reason,
      telemetryDefaultMode: trimString(source.telemetryDefaultMode),
      telemetryPreference: trimString(source.telemetryPreference),
      telemetryReason: trimString(source.telemetryReason),
      telemetrySource: trimString(source.telemetrySource),
      logsExporterEndpoint: trimString(source.logsExporterEndpoint),
      lastLogExport,
    };
    return {
      ...status,
      detail: telemetryStatusDetail(reason, status),
    };
  }

  function resolveTelemetryStatus() {
    const otel = globalRef.__fishystuffTelemetry;
    if (otel && typeof otel.status === "function") {
      try {
        return normalizeTelemetryStatus(otel.status());
      } catch (_error) {
        return normalizeTelemetryStatus({ reason: "telemetry-status-error" });
      }
    }
    return normalizeTelemetryStatus({ reason: "telemetry-unavailable" });
  }

  function current() {
    const active = activeWarnings();
    const preferences = preferencesDecision();
    const hidden = preferences.hideNavMenu === true;
    const forcedVisible = active.some(issueForcesMenuVisible);
    const activeErrorCount = active.filter((issue) => issue.severity === "error").length;
    const telemetry = resolveTelemetryStatus();
    return {
      version: 1,
      issueCount: issues.size,
      activeWarningCount: active.length,
      activeErrorCount,
      hiddenByUserSetting: hidden,
      menuForcedVisible: forcedVisible,
      navMenuVisible: active.length > 0 && (!hidden || forcedVisible),
      issues: sortedIssues(),
      activeWarnings: active,
      preferences,
      telemetry,
    };
  }

  function issueSignal(issue = null) {
    if (!issue) {
      return {
        visible: false,
        id: "",
        severity: "warning",
        severityLabel: "",
        source: "",
        title: "",
        detail: "",
        copyText: "",
        copyAvailable: false,
        count: 0,
        countLabel: "",
      };
    }
    return {
      visible: true,
      id: issue.id,
      severity: issue.severity,
      severityLabel: translate(`health.issue.severity.${issue.severity}`, issue.severity),
      source: issue.source,
      title: issue.title,
      detail: issue.detail,
      copyText: issue.copyText,
      copyAvailable: Boolean(issue.copyText),
      count: issue.count,
      countLabel: `${issue.count}x`,
    };
  }

  function summaryLabel(snapshot) {
    return snapshot.activeWarningCount === 1
      ? translate("health.issue.active_warning.one", "1 active warning")
      : translate("health.issue.active_warning.many", `${snapshot.activeWarningCount} active warnings`, {
          count: snapshot.activeWarningCount,
        });
  }

  function hiddenLabel(snapshot) {
    if (snapshot.menuForcedVisible) {
      return translate("health.menu.visible_for_errors", "Visible while errors are active");
    }
    return snapshot.hiddenByUserSetting
      ? translate("health.menu.hidden_in_settings", "Hidden in settings")
      : translate("health.menu.visible_when_warnings", "Visible when warnings are active");
  }

  function menuSnapshot(snapshot = current()) {
    const telemetry = snapshot.telemetry || resolveTelemetryStatus();
    const preferences = snapshot.preferences || preferencesDecision();
    const canDescribe = (snapshot.activeWarnings || [])
      .some((issue) => issue.reportable !== false);
    const canSend = telemetry.diagnosticReportsEnabled === true && canDescribe;
    const descriptionMaxLength =
      globalRef.__fishystuffDiagnosticReportContract?.maxDescriptionLength;
    if (!Number.isInteger(descriptionMaxLength)) {
      throw new Error("Diagnostic report description contract is unavailable.");
    }
    return {
      version: 2,
      issueCount: snapshot.issueCount,
      activeWarningCount: snapshot.activeWarningCount,
      activeErrorCount: snapshot.activeErrorCount,
      navMenuVisible: snapshot.navMenuVisible,
      menuForcedVisible: snapshot.menuForcedVisible,
      hiddenByUserSetting: snapshot.hiddenByUserSetting,
      menuEnabledByUser: snapshot.hiddenByUserSetting !== true,
      preferencesCanChange: preferences.canChange === true,
      preferencesStatus: preferencesStatus(preferences),
      summary: summaryLabel(snapshot),
      hiddenLabel: hiddenLabel(snapshot),
      canDescribe,
      canSend,
      descriptionMaxLength,
      sendTitle: canSend
        ? translate("health.report.send_title", "Preview selected issues and description")
        : telemetry.detail,
      visibleIssues: (snapshot.activeWarnings || []).slice(0, 5).map(issueSignal),
      diagnosticPreview: manualReportPreview
        ? {
            reportId: manualReportPreview.report.report.id,
            json: JSON.stringify(manualReportPreview.report, null, 2),
          }
        : null,
    };
  }

  function dispatchDomChange(snapshot, reason) {
    globalRef.dispatchEvent?.(
      new CustomEvent(CHANGE_EVENT, {
        detail: {
          reason: trimString(reason) || "update",
          snapshot,
        },
      }),
    );
  }

  function emitChange(reason) {
    const snapshot = current();
    for (const listener of subscribers) {
      try {
        listener(snapshot, reason);
      } catch (_error) {
      }
    }
    dispatchDomChange(snapshot, reason);
    renderNavRoots(snapshot);
    return snapshot;
  }

  function upsertIssue(value, options = {}) {
    const nextIssue = normalizeIssue(value);
    const previous = issues.get(nextIssue.id);
    const shouldPreserveDismissed = options.preserveDismissed !== false;
    const merged = previous
      ? {
          ...previous,
          ...nextIssue,
          createdAt: previous.createdAt || nextIssue.createdAt,
          updatedAt: nextIssue.updatedAt || nowIso(),
          count: Math.max(1, previous.count + 1),
          dismissed: shouldPreserveDismissed ? previous.dismissed : nextIssue.dismissed,
          copyText: nextIssue.copyText || previous.copyText || "",
        }
      : nextIssue;
    issues.set(merged.id, merged);
    return emitChange("issue");
  }

  function dismissIssue(id) {
    const normalizedId = trimString(id);
    const issue = issues.get(normalizedId);
    if (!issue) {
      return current();
    }
    issues.set(normalizedId, { ...issue, dismissed: true, updatedAt: nowIso() });
    return emitChange("dismiss");
  }

  function dismissAll() {
    let changed = false;
    for (const [id, issue] of issues.entries()) {
      if (issueIsWarning(issue) && !issue.dismissed) {
        issues.set(id, { ...issue, dismissed: true, updatedAt: nowIso() });
        changed = true;
      }
    }
    return changed ? emitChange("dismiss-all") : current();
  }

  function dismissIssues(issueIds, reason = "dismiss-selected") {
    const ids = new Set((Array.isArray(issueIds) ? issueIds : [])
      .map((id) => trimString(id))
      .filter(Boolean));
    let changed = false;
    for (const id of ids) {
      const issue = issues.get(id);
      if (issue && issueIsWarning(issue) && !issue.dismissed) {
        issues.set(id, { ...issue, dismissed: true, updatedAt: nowIso() });
        changed = true;
      }
    }
    return changed ? emitChange(reason) : current();
  }

  function clearIssue(id) {
    const normalizedId = trimString(id);
    if (!issues.delete(normalizedId)) {
      return current();
    }
    return emitChange("clear");
  }

  function clearAll(reason) {
    issues.clear();
    return emitChange(reason || "clear-all");
  }

  function syncSourceIssues(source, nextIssues) {
    const normalizedSource = trimString(source) || DEFAULT_SOURCE;
    const incoming = Array.isArray(nextIssues) ? nextIssues : [];
    const nextIds = new Set();
    for (const issue of incoming) {
      const normalizedIssue = normalizeIssue({
        ...issue,
        source: trimString(issue?.source) || normalizedSource,
      });
      nextIds.add(normalizedIssue.id);
      const previous = issues.get(normalizedIssue.id);
      issues.set(normalizedIssue.id, {
        ...(previous || normalizedIssue),
        ...normalizedIssue,
        createdAt: previous?.createdAt || normalizedIssue.createdAt,
        count: previous ? previous.count : normalizedIssue.count,
        dismissed: previous?.dismissed === true,
        copyText: normalizedIssue.copyText || previous?.copyText || "",
      });
    }
    for (const [id, issue] of issues.entries()) {
      if (issue.source === normalizedSource && !nextIds.has(id)) {
        issues.delete(id);
      }
    }
    return emitChange("sync-source");
  }

  function clearSourceIssues(source) {
    const normalizedSource = trimString(source) || DEFAULT_SOURCE;
    let changed = false;
    for (const [id, issue] of issues.entries()) {
      if (issue.source === normalizedSource) {
        issues.delete(id);
        changed = true;
      }
    }
    return changed ? emitChange("clear-source") : current();
  }

  function registerSource(source, defaults = {}) {
    const normalizedSource = trimString(source) || DEFAULT_SOURCE;
    const baseIssue = isPlainObject(defaults) ? cloneJson(defaults) : {};
    function withSource(issue) {
      return {
        ...baseIssue,
        ...(isPlainObject(issue) ? issue : {}),
        source: trimString(issue?.source) || normalizedSource,
      };
    }
    return Object.freeze({
      source: normalizedSource,
      report(issue, options) {
        return upsertIssue(withSource(issue), options);
      },
      sync(nextIssues) {
        return syncSourceIssues(
          normalizedSource,
          (Array.isArray(nextIssues) ? nextIssues : []).map(withSource),
        );
      },
      clear(id) {
        return clearIssue(id);
      },
      clearAll() {
        return clearSourceIssues(normalizedSource);
      },
    });
  }


  function buildDiagnosticReport(selectedIssues, options = {}) {
    const active = Array.isArray(selectedIssues) && selectedIssues.length
      ? selectedIssues
      : activeWarnings();
    const contract = globalRef.__fishystuffDiagnosticReportContract;
    if (!contract || typeof contract.create !== "function") {
      throw new Error("FishyStuff diagnostic report contract is unavailable");
    }
    const preferences = preferencesDecision();
    return contract.create({
      issues: active,
      issueCount: active.length,
      description: options.description,
      trigger: options.trigger,
      page: { path: trimString(globalRef.location?.pathname) },
      hiddenByUserSetting: preferences.hideNavMenu === true,
    });
  }

  function reportFailureMessage(reason, telemetry) {
    if (reason === "telemetry-export-failed" || telemetry?.lastLogExport?.ok === false) {
      return translate(
        "health.report.failure.delivery",
        "Telemetry report could not be delivered.",
      );
    }
    if (reason === "opt-in-required" || reason === "disabled-by-user" || reason === "disabled-by-query") {
      return translate("health.report.failure.telemetry_off", "Telemetry is off for this page.");
    }
    if (reason === "logs-disabled" || reason === "missing-logs-exporter" || reason === "logs-unavailable") {
      return translate(
        "health.report.failure.logs_unavailable",
        "Telemetry logs are unavailable right now.",
      );
    }
    return translate(
      "health.report.failure.telemetry_unavailable",
      "Telemetry is unavailable right now.",
    );
  }

  function markIssueReportState(selected, patch, reason) {
    const ids = new Set((Array.isArray(selected) ? selected : [])
      .map((issue) => trimString(issue?.id))
      .filter(Boolean));
    let changed = false;
    for (const id of ids) {
      const issue = issues.get(id);
      if (!issue) {
        continue;
      }
      issues.set(id, {
        ...issue,
        ...patch,
        updatedAt: nowIso(),
      });
      changed = true;
    }
    return changed ? emitChange(reason) : current();
  }

  async function sendDiagnosticReport(selectedIssues, options = {}) {
    const selected = reportableWarnings(selectedIssues);
    const notify = options.notify !== false;
    const dismissOnSuccess = options.dismissOnSuccess !== false;
    if (!selected.length) {
      return { sent: false, reason: "no-active-warnings", report: null, telemetry: resolveTelemetryStatus() };
    }
    const report = options.preparedReport || buildDiagnosticReport(selected, {
      description: options.description,
      trigger: options.trigger,
    });
    const otel = globalRef.__fishystuffTelemetry;
    if (!otel || typeof otel.emitDiagnosticReport !== "function") {
      const telemetry = resolveTelemetryStatus();
      if (notify) {
        showToast("error", reportFailureMessage("telemetry-unavailable", telemetry));
      }
      emitChange("report-failed");
      return { sent: false, reason: "telemetry-unavailable", report, telemetry };
    }
    let result;
    try {
      result = await otel.emitDiagnosticReport(report);
    } catch (_error) {
      result = {
        sent: false,
        reason: "telemetry-export-failed",
        status: {
          ...resolveTelemetryStatus(),
          lastLogExport: {
            ok: false,
            error: "diagnostic report export failed",
          },
        },
      };
    }
    const telemetry = normalizeTelemetryStatus(result?.status || resolveTelemetryStatus());
    if (result?.sent !== true) {
      const reason = trimString(result?.reason) || "telemetry-unavailable";
      if (notify) {
        showToast("error", reportFailureMessage(reason, telemetry));
      }
      emitChange("report-failed");
      return { sent: false, reason, report, telemetry };
    }
    if (dismissOnSuccess) {
      manualReportPreview = null;
      dismissIssues(selected.map((issue) => issue.id), "report-sent");
    } else {
      emitChange("report-sent");
    }
    if (notify) {
      showToast("success", translate("health.report.success.sent", "Diagnostic report sent."));
    }
    return { sent: true, reason: "sent", report, telemetry };
  }

  function previewManualReport(issueIds = null, description = "") {
    const selectedIds = Array.isArray(issueIds) ? new Set(issueIds) : null;
    const selected = reportableWarnings(activeWarnings()).filter(
      (issue) => selectedIds === null || selectedIds.has(issue.id),
    );
    if (!selected.length) {
      return { previewed: false, reason: "no-active-warnings", report: null };
    }
    const report = freezeJson(buildDiagnosticReport(selected, {
      description,
      trigger: "manual",
    }));
    manualReportPreview = Object.freeze({
      report,
      issueIds: Object.freeze(selected.map((issue) => issue.id)),
    });
    emitChange("manual-report-preview");
    return { previewed: true, reason: "preview-ready", report };
  }

  function cancelManualReport() {
    if (!manualReportPreview) return false;
    manualReportPreview = null;
    emitChange("manual-report-preview-cancelled");
    return true;
  }

  async function confirmManualReport() {
    const preview = manualReportPreview;
    if (!preview) {
      return { sent: false, reason: "preview-required", report: null, telemetry: resolveTelemetryStatus() };
    }
    const selected = preview.issueIds
      .map((id) => issues.get(id))
      .filter(Boolean);
    return sendDiagnosticReport(selected, {
      dismissOnSuccess: true,
      notify: true,
      trigger: "manual-confirmed",
      preparedReport: preview.report,
    });
  }

  function sendManualReport(description, issueIds = null) {
    return previewManualReport(issueIds, description);
  }

  function showToast(tone, message) {
    const toast = globalRef.__fishystuffToast;
    if (!toast || !message) {
      return;
    }
    if (typeof toast[tone] === "function") {
      toast[tone](message);
      return;
    }
    if (typeof toast.show === "function") {
      toast.show({ tone, message });
    }
  }

  const navRoots = new Set();

  function text(node, value) {
    if (!node) {
      return;
    }
    const next = String(value ?? "");
    if (node.textContent !== next) {
      node.textContent = next;
    }
  }


  function createFallbackNavRoot(rootDocument) {
    if (!rootDocument?.body || typeof rootDocument.createElement !== "function") {
      return null;
    }
    const existing = rootDocument.getElementById?.("fishystuff-page-health-fallback-menu");
    if (existing) {
      return existing;
    }
    const root = rootDocument.createElement("fishy-page-health-menu");
    root.id = "fishystuff-page-health-fallback-menu";
    root.className = "dropdown dropdown-end not-prose";
    root.style.position = "fixed";
    root.style.top = "0.75rem";
    root.style.right = "0.75rem";
    root.style.zIndex = "2100";
    root.hidden = true;
    root.setAttribute("data-page-health-menu", "");
    root.setAttribute("data-health-fallback-menu", "");
    rootDocument.body.appendChild(root);
    return root;
  }

  function notifyLanguageChange() {
    const snapshot = current();
    for (const listener of subscribers) {
      try {
        listener(snapshot, "language");
      } catch (_error) {
      }
    }
    dispatchDomChange(snapshot, "language");
    renderNavRoots(snapshot);
  }

  function copyTextForIssue(issue) {
    return trimString(issue?.copyText || issue?.detail || issue?.title);
  }

  async function copyIssueText(id) {
    const issue = issues.get(trimString(id));
    const value = copyTextForIssue(issue);
    if (!value) {
      return false;
    }
    const toast = globalRef.__fishystuffToast;
    const options = {
      success: translate("health.issue.copy_success", "Error details copied."),
      error: translate(
        "health.issue.copy_failure",
        "Clipboard access is unavailable in this browser.",
      ),
    };
    if (toast && typeof toast.copyText === "function") {
      return toast.copyText(value, options);
    }
    try {
      const clipboard = globalRef.navigator?.clipboard;
      if (!clipboard || typeof clipboard.writeText !== "function") {
        throw new Error("Clipboard API unavailable");
      }
      await clipboard.writeText(value);
      showToast("success", options.success);
      return true;
    } catch (_error) {
      showToast("error", options.error);
      return false;
    }
  }

  function handleNavEvent(event) {
    const events = event.currentTarget?.constructor?.events;
    if (!events) {
      return;
    }
    if (event.type === events.copyIssue) {
      Promise.resolve(copyIssueText(event.detail?.issueId)).catch(() => {});
    } else if (event.type === events.dismissIssue) {
      dismissIssue(event.detail?.issueId);
    } else if (event.type === events.dismissAll) {
      dismissAll();
    } else if (event.type === events.sendReport) {
      previewManualReport(event.detail?.issueIds, event.detail?.description);
    } else if (event.type === events.confirmReport) {
      Promise.resolve(confirmManualReport()).finally(() => {
        renderNavRoots(current());
      });
    } else if (event.type === events.cancelReport) {
      cancelManualReport();
    } else if (event.type === events.menuVisibilityChange) {
      setMenuHidden(event.detail?.enabled !== true);
    }
  }

  function renderNavRoot(root, snapshot) {
    root.snapshot = menuSnapshot(snapshot);
  }

  function renderNavRoots(snapshot = current()) {
    for (const root of navRoots) {
      renderNavRoot(root, snapshot);
    }
  }

  function bindNav(root) {
    if (!captureActive || !root || typeof root.addEventListener !== "function") {
      return null;
    }
    if (navRoots.has(root)) {
      return root;
    }
    const events = root.constructor?.events;
    if (!events) {
      return null;
    }
    navRoots.add(root);
    Object.values(events).forEach((type) => root.addEventListener(type, handleNavEvent));
    renderNavRoot(root, current());
    return root;
  }

  function unbindNav(root) {
    if (!navRoots.has(root)) return false;
    const events = root?.constructor?.events;
    if (events && typeof root.removeEventListener === "function") {
      Object.values(events).forEach((type) => root.removeEventListener(type, handleNavEvent));
    }
    navRoots.delete(root);
    return true;
  }

  function bindDocument(rootDocument = globalRef.document) {
    if (
      !captureActive
      || !rootDocument
      || typeof rootDocument.querySelectorAll !== "function"
    ) {
      return;
    }
    let roots = Array.from(rootDocument.querySelectorAll("[data-page-health-menu]"));
    if (!roots.length) {
      const fallback = createFallbackNavRoot(rootDocument);
      roots = fallback ? [fallback] : [];
    }
    roots.forEach((root) => {
      if (bindNav(root)) {
        return;
      }
      const generation = captureGeneration;
      globalRef.customElements?.whenDefined?.("fishy-page-health-menu").then(() => {
        if (captureActive && generation === captureGeneration) bindNav(root);
      });
    });
    const snapshot = current();
    renderNavRoots(snapshot);
  }

  function subscribe(listener) {
    if (typeof listener !== "function") {
      return function () {};
    }
    subscribers.add(listener);
    listener(current(), "subscribe");
    return function () {
      subscribers.delete(listener);
    };
  }

  function isErrorLike(value) {
    if (!value || typeof value !== "object") {
      return false;
    }
    return Object.prototype.toString.call(value) === "[object Error]"
      || value instanceof Error
      || ("message" in value && ("stack" in value || "name" in value));
  }

  function errorLikeText(value) {
    const stack = trimString(value?.stack);
    if (stack) {
      return stack;
    }
    const name = trimString(value?.name || value?.constructor?.name) || "Error";
    const message = trimString(value?.message);
    return message ? `${name}: ${message}` : name;
  }

  function errorSummaryText(value) {
    if (isErrorLike(value)) {
      const name = trimString(value?.name || value?.constructor?.name) || "Error";
      const message = trimString(value?.message);
      return message ? `${name}: ${message}` : name;
    }
    return compactText(consoleValueText(value), 140);
  }

  function jsonConsoleText(value) {
    const seen = new Set();
    try {
      return JSON.stringify(value, (_key, nested) => {
        if (typeof nested === "bigint") {
          return `${nested.toString()}n`;
        }
        if (typeof nested === "function") {
          return `[Function${nested.name ? ` ${nested.name}` : ""}]`;
        }
        if (nested && typeof nested === "object") {
          if (seen.has(nested)) {
            return "[Circular]";
          }
          seen.add(nested);
        }
        return nested;
      });
    } catch (_error) {
      return "";
    }
  }

  function consoleValueText(value) {
    if (isErrorLike(value)) {
      return errorLikeText(value);
    }
    if (typeof value === "string") {
      return value;
    }
    if (value === null || value === undefined) {
      return String(value);
    }
    if (typeof value === "symbol") {
      return value.toString();
    }
    if (typeof value === "function") {
      return `[Function${value.name ? ` ${value.name}` : ""}]`;
    }
    if (typeof value === "object") {
      return jsonConsoleText(value) || String(value);
    }
    return String(value);
  }

  function consoleArgsText(args) {
    return boundedText(
      (Array.isArray(args) ? args : [])
        .map(consoleValueText)
        .filter(Boolean)
        .join(" "),
      MAX_COPY_TEXT_CHARS,
    ) || "console.error";
  }

  function issueFromConsoleErrorArgs(args) {
    const originalErrorText = consoleArgsText(args);
    const location = globalRef.location || {};
    return {
      id: `browser-console:console-error:${textFingerprint([
        trimString(location.pathname),
        originalErrorText,
      ].join("\n"))}`,
      severity: "error",
      source: "browser-console",
      category: "console",
      title: translate("health.console_error.title", "Console error"),
      detail: compactText(originalErrorText, MAX_DETAIL_CHARS),
      copyText: originalErrorText,
      context: {
        argumentCount: Array.isArray(args) ? args.length : 0,
        href: trimString(location.href),
        path: trimString(location.pathname),
        originalErrorText,
      },
    };
  }

  function normalizedMethod(value) {
    return (trimString(value) || "GET").toUpperCase();
  }

  function fetchRequestInfo(input, init) {
    const source = input && typeof input === "object" ? input : {};
    return {
      method: normalizedMethod(init?.method || source.method || "GET"),
      url: trimString(source.url || source.href || input),
    };
  }

  function compactStatus(status, statusText) {
    const numeric = Number.parseInt(status, 10);
    const label = trimString(statusText);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      return label || "network error";
    }
    return label ? `${numeric} ${label}` : String(numeric);
  }

  function shouldReportHttpStatus(status) {
    const numeric = Number.parseInt(status, 10);
    return Number.isFinite(numeric) && numeric >= 500;
  }

  function isFetchAbort(input, init, error) {
    const source = input && typeof input === "object" ? input : {};
    return error?.name === "AbortError"
      || init?.signal?.aborted === true
      || source.signal?.aborted === true;
  }

  function networkErrorIssue({
    transport,
    method,
    url,
    status,
    statusText,
    error,
    eventType,
    responseUrl,
  }) {
    const normalizedTransport = trimString(transport) || "network";
    const methodLabel = normalizedMethod(method);
    const normalizedUrl = trimString(responseUrl || url);
    const pageUrl = trimString(globalRef.location?.href);
    const hasStatus = shouldReportHttpStatus(status);
    const statusLabel = hasStatus ? compactStatus(status, statusText) : "";
    const errorText = error === undefined || error === null
      ? ""
      : boundedText(
          isErrorLike(error) ? errorLikeText(error) : consoleValueText(error),
          MAX_CONTEXT_TEXT_CHARS,
        );
    const errorSummary = error === undefined || error === null
      ? ""
      : errorSummaryText(error);
    const eventLabel = trimString(eventType);
    const detail = compactText(
      [
        methodLabel,
        normalizedUrl,
        statusLabel || errorSummary || eventLabel,
      ].filter(Boolean).join(" "),
      MAX_DETAIL_CHARS,
    );
    const originalErrorText = boundedText(
      [
        `${normalizedTransport.toUpperCase()} request failed`,
        methodLabel ? `method=${methodLabel}` : "",
        hasStatus ? `status=${statusLabel}` : "",
        eventLabel ? `event=${eventLabel}` : "",
        normalizedUrl ? `url=${normalizedUrl}` : "",
        pageUrl ? `page=${pageUrl}` : "",
        errorText ? `error=${errorText}` : "",
      ].filter(Boolean).join("\n"),
      MAX_COPY_TEXT_CHARS,
    );
    return {
      id: `browser-network:${normalizedTransport}:${textFingerprint([
        methodLabel,
        normalizedUrl,
        statusLabel || eventLabel || errorSummary,
      ].join("\n"))}`,
      severity: "error",
      source: "browser-network",
      category: normalizedTransport,
      title: translate("health.network_error.title", "Network request failed"),
      detail,
      copyText: originalErrorText,
      context: {
        transport: normalizedTransport,
        method: methodLabel,
        url: normalizedUrl,
        status: hasStatus ? Number.parseInt(status, 10) : null,
        statusText: trimString(statusText),
        eventType: eventLabel,
        pageUrl,
        originalErrorText,
      },
    };
  }

  function reportNetworkIssue(value) {
    upsertIssue(networkErrorIssue(value), { preserveDismissed: false });
  }

  function targetTagName(target) {
    return trimString(target?.tagName || target?.nodeName).toLowerCase();
  }

  function targetResourceUrl(target) {
    for (const candidate of [
      target?.currentSrc,
      target?.src,
      target?.href,
      target?.data,
      target?.action,
      target?.poster,
    ]) {
      const value = typeof candidate === "string"
        ? candidate
        : typeof candidate?.baseVal === "string"
          ? candidate.baseVal
          : "";
      const normalized = trimString(value);
      if (normalized) return normalized;
    }
    return "";
  }

  function targetDescriptor(target) {
    const tag = targetTagName(target) || "resource";
    const id = trimString(target?.id);
    const className = typeof target?.className === "string" ? trimString(target.className) : "";
    const suffix = [
      id ? `#${id}` : "",
      className ? `.${className.split(/\s+/).filter(Boolean).slice(0, 3).join(".")}` : "",
    ].join("");
    return `${tag}${suffix}`;
  }

  function isResourceErrorEvent(event) {
    const target = event?.target;
    if (!target || target === globalRef || target === globalRef.window || target === globalRef.document) {
      return false;
    }
    return !("message" in event) || !trimString(event.message);
  }

  function isIgnoredResourceErrorEvent(event) {
    const target = event?.target;
    return targetTagName(target) === "use"
      && targetResourceUrl(target).startsWith("#");
  }

  function issueFromResourceErrorEvent(event) {
    const target = event?.target;
    const descriptor = targetDescriptor(target);
    const resourceUrl = targetResourceUrl(target);
    const pageUrl = trimString(globalRef.location?.href);
    const originalErrorText = [
      "Resource load error",
      descriptor ? `target=${descriptor}` : "",
      resourceUrl ? `url=${resourceUrl}` : "",
      pageUrl ? `page=${pageUrl}` : "",
    ].filter(Boolean).join("\n");
    return {
      id: `browser-resource:error:${textFingerprint([
        trimString(globalRef.location?.pathname),
        descriptor,
        resourceUrl,
      ].join("\n"))}`,
      severity: "error",
      source: "browser-resource",
      category: "resource",
      title: translate("health.resource_error.title", "Resource load error"),
      detail: compactText(resourceUrl ? `${descriptor} ${resourceUrl}` : descriptor, MAX_DETAIL_CHARS),
      copyText: originalErrorText,
      context: {
        target: descriptor,
        tagName: targetTagName(target),
        resourceUrl,
        pageUrl,
      },
    };
  }

  function issueFromErrorEvent(event) {
    const error = event?.error;
    const message = trimString(error?.message || event?.message || "Unhandled page error");
    const location = [event?.filename, event?.lineno, event?.colno]
      .map((part) => trimString(part))
      .filter(Boolean)
      .join(":");
    const originalErrorText = boundedText(
      error?.stack || (location ? `${message} (${location})` : message),
    );
    return {
      id: `browser:error:${message}:${location}`.toLowerCase(),
      severity: "error",
      source: "browser",
      category: "runtime",
      title: translate("health.runtime_error.title", "Page script error"),
      detail: location ? `${message} (${location})` : message,
      copyText: originalErrorText,
      context: {
        filename: trimString(event?.filename),
        lineno: event?.lineno || null,
        colno: event?.colno || null,
        stack: boundedText(error?.stack || "", MAX_CONTEXT_TEXT_CHARS),
        originalErrorText,
      },
    };
  }

  function handleErrorEvent(event) {
    if (isIgnoredResourceErrorEvent(event)) return;
    upsertIssue(
      isResourceErrorEvent(event)
        ? issueFromResourceErrorEvent(event)
        : issueFromErrorEvent(event),
      { preserveDismissed: false },
    );
  }

  function issueFromRejection(event) {
    const reason = event?.reason;
    const message = trimString(reason?.message || reason || "Unhandled promise rejection");
    const originalErrorText = boundedText(
      isErrorLike(reason) ? errorLikeText(reason) : consoleValueText(reason),
    );
    return {
      id: `browser:rejection:${message}`.toLowerCase(),
      severity: "error",
      source: "browser",
      category: "runtime",
      title: translate("health.unhandled_rejection.title", "Unhandled page error"),
      detail: message,
      copyText: originalErrorText,
      context: {
        stack: boundedText(reason?.stack || "", MAX_CONTEXT_TEXT_CHARS),
        originalErrorText,
      },
    };
  }

  function installConsoleErrorCapture() {
    if (globalRef[CONSOLE_ERROR_HOOK_KEY] !== undefined) {
      return false;
    }
    const consoleRef = globalRef.console;
    if (!consoleRef || typeof consoleRef.error !== "function") {
      return false;
    }
    const originalError = consoleRef.error;
    const wrapper = function pageHealthConsoleError(...args) {
      if (captureActive) {
        try {
          upsertIssue(issueFromConsoleErrorArgs(args), { preserveDismissed: false });
        } catch (_error) {
        }
      }
      return originalError.apply(consoleRef, args);
    };
    consoleRef.error = wrapper;
    consoleErrorHook = Object.freeze({ consoleRef, originalError, wrapper });
    globalRef[CONSOLE_ERROR_HOOK_KEY] = wrapper;
    return true;
  }

  function uninstallConsoleErrorCapture() {
    const hook = consoleErrorHook;
    if (!hook) return false;
    if (hook.consoleRef.error === hook.wrapper) {
      hook.consoleRef.error = hook.originalError;
    }
    if (globalRef[CONSOLE_ERROR_HOOK_KEY] === hook.wrapper) {
      delete globalRef[CONSOLE_ERROR_HOOK_KEY];
    }
    consoleErrorHook = null;
    return true;
  }

  function installFetchErrorCapture() {
    if (globalRef[FETCH_HOOK_KEY] !== undefined || typeof globalRef.fetch !== "function") {
      return false;
    }
    const originalFetch = globalRef.fetch;
    const wrapper = function pageHealthFetch(input, init) {
      const request = fetchRequestInfo(input, init);
      const requestGeneration = captureGeneration;
      return originalFetch.apply(this, arguments).then(
        (response) => {
          try {
            if (
              captureActive
              && requestGeneration === captureGeneration
              && response
              && shouldReportHttpStatus(response.status)
            ) {
              reportNetworkIssue({
                transport: "fetch",
                method: request.method,
                url: request.url,
                responseUrl: response.url,
                status: response.status,
                statusText: response.statusText,
              });
            }
          } catch (_error) {
          }
          return response;
        },
        (error) => {
          try {
            if (
              captureActive
              && requestGeneration === captureGeneration
              && !isFetchAbort(input, init, error)
            ) {
              reportNetworkIssue({
                transport: "fetch",
                method: request.method,
                url: request.url,
                error,
              });
            }
          } catch (_captureError) {
          }
          throw error;
        },
      );
    };
    globalRef.fetch = wrapper;
    fetchHook = Object.freeze({ originalFetch, wrapper });
    globalRef[FETCH_HOOK_KEY] = wrapper;
    return true;
  }

  function uninstallFetchErrorCapture() {
    const hook = fetchHook;
    if (!hook) return false;
    if (globalRef.fetch === hook.wrapper) {
      globalRef.fetch = hook.originalFetch;
    }
    if (globalRef[FETCH_HOOK_KEY] === hook.wrapper) {
      delete globalRef[FETCH_HOOK_KEY];
    }
    fetchHook = null;
    return true;
  }

  function xhrState(xhr) {
    if (!xhr || typeof xhr !== "object") {
      return {};
    }
    if (!isPlainObject(xhr[XHR_STATE_KEY])) {
      try {
        xhr[XHR_STATE_KEY] = {};
      } catch (_error) {
        return {};
      }
    }
    return xhr[XHR_STATE_KEY];
  }

  function installXhrErrorCapture() {
    const Xhr = globalRef.XMLHttpRequest;
    const proto = Xhr?.prototype;
    if (
      globalRef[XHR_HOOK_KEY] !== undefined
      || !proto
      || typeof proto.open !== "function"
      || typeof proto.send !== "function"
    ) {
      return false;
    }

    const originalOpen = proto.open;
    const originalSend = proto.send;

    const openWrapper = function pageHealthXhrOpen(method, url) {
      const state = xhrState(this);
      state.method = normalizedMethod(method);
      state.url = trimString(url);
      state.reportedFailure = false;
      return originalOpen.apply(this, arguments);
    };

    const sendWrapper = function pageHealthXhrSend() {
      const xhr = this;
      const state = xhrState(xhr);
      const requestGeneration = captureGeneration;
      const reportHttpStatus = () => {
        if (
          !captureActive
          || requestGeneration !== captureGeneration
          || state.reportedFailure
          || !shouldReportHttpStatus(xhr.status)
        ) {
          return;
        }
        state.reportedFailure = true;
        reportNetworkIssue({
          transport: "xhr",
          method: state.method,
          url: state.url,
          responseUrl: xhr.responseURL,
          status: xhr.status,
          statusText: xhr.statusText,
        });
      };
      const reportEventFailure = (event) => {
        if (
          !captureActive
          || requestGeneration !== captureGeneration
          || state.reportedFailure
        ) {
          return;
        }
        state.reportedFailure = true;
        reportNetworkIssue({
          transport: "xhr",
          method: state.method,
          url: state.url,
          responseUrl: xhr.responseURL,
          status: xhr.status,
          statusText: xhr.statusText,
          eventType: event?.type,
        });
      };

      try {
        xhr.addEventListener?.("loadend", reportHttpStatus, { once: true });
        xhr.addEventListener?.("error", reportEventFailure, { once: true });
        xhr.addEventListener?.("timeout", reportEventFailure, { once: true });
        xhr.addEventListener?.("abort", reportEventFailure, { once: true });
      } catch (_error) {
      }

      return originalSend.apply(xhr, arguments);
    };

    proto.open = openWrapper;
    proto.send = sendWrapper;
    xhrHook = Object.freeze({
      originalOpen,
      originalSend,
      openWrapper,
      proto,
      sendWrapper,
    });
    globalRef[XHR_HOOK_KEY] = xhrHook;
    return true;
  }

  function uninstallXhrErrorCapture() {
    const hook = xhrHook;
    if (!hook) return false;
    if (hook.proto.open === hook.openWrapper) {
      hook.proto.open = hook.originalOpen;
    }
    if (hook.proto.send === hook.sendWrapper) {
      hook.proto.send = hook.originalSend;
    }
    if (globalRef[XHR_HOOK_KEY] === hook) {
      delete globalRef[XHR_HOOK_KEY];
    }
    xhrHook = null;
    return true;
  }

  function installNetworkErrorCapture() {
    const fetchInstalled = installFetchErrorCapture();
    const xhrInstalled = installXhrErrorCapture();
    return fetchInstalled || xhrInstalled;
  }

  function handleUnhandledRejection(event) {
    upsertIssue(issueFromRejection(event), { preserveDismissed: false });
  }

  function handleTelemetryStatusChange() {
    emitChange("telemetry-status");
  }

  function handleDocumentReady() {
    waitingForDocumentReady = false;
    if (captureActive) bindDocument();
  }

  function startCaptureService() {
    if (captureActive) return false;
    captureActive = true;
    captureGeneration += 1;
    globalRef.addEventListener?.("error", handleErrorEvent, true);
    globalRef.addEventListener?.("unhandledrejection", handleUnhandledRejection);
    globalRef.addEventListener?.(TELEMETRY_STATUS_EVENT, handleTelemetryStatusChange);
    globalRef.addEventListener?.(LANGUAGE_CHANGE_EVENT, notifyLanguageChange);
    subscribePreferences();

    installConsoleErrorCapture();
    installNetworkErrorCapture();

    if (globalRef.document?.readyState === "loading") {
      waitingForDocumentReady = true;
      globalRef.document.addEventListener("DOMContentLoaded", handleDocumentReady, { once: true });
    } else {
      bindDocument();
    }
    return true;
  }

  function dispose() {
    if (!captureActive) return false;
    captureActive = false;
    captureGeneration += 1;
    manualReportPreview = null;
    globalRef.removeEventListener?.("error", handleErrorEvent, true);
    globalRef.removeEventListener?.("unhandledrejection", handleUnhandledRejection);
    globalRef.removeEventListener?.(TELEMETRY_STATUS_EVENT, handleTelemetryStatusChange);
    globalRef.removeEventListener?.(LANGUAGE_CHANGE_EVENT, notifyLanguageChange);
    if (waitingForDocumentReady) {
      globalRef.document?.removeEventListener?.("DOMContentLoaded", handleDocumentReady);
      waitingForDocumentReady = false;
    }
    for (const root of Array.from(navRoots)) unbindNav(root);
    subscribers.clear();
    if (typeof unsubscribePreferences === "function") unsubscribePreferences();
    unsubscribePreferences = null;
    uninstallConsoleErrorCapture();
    uninstallFetchErrorCapture();
    uninstallXhrErrorCapture();
    return true;
  }

  startCaptureService();

  globalRef.__fishystuffPageHealth = Object.freeze({
    CHANGE_EVENT,
    TELEMETRY_STATUS_EVENT,
    bindDocument,
    bindNav,
    bindPreferencesRepository,
    buildDiagnosticReport,
    clearAll,
    clearIssue,
    cancelManualReport,
    confirmManualReport,
    copyIssueText,
    current,
    dispose,
    dismissAll,
    dismissIssues,
    dismissIssue,
    installConsoleErrorCapture,
    installNetworkErrorCapture,
    registerSource,
    previewManualReport,
    reportIssue: upsertIssue,
    sendDiagnosticReport,
    sendManualReport,
    setMenuHidden,
    start: startCaptureService,
    subscribe,
    syncSourceIssues,
  });
})(typeof window !== "undefined" ? window : globalThis);
